import { phoneNumberSchema } from "@/components/Forms/schema/phoneNumberSchema.ts";
import useAuthenticatedFetch from "@/hooks/useAuthenticatedFetch.ts";
import { apiUrl } from "@/utils/api.ts";
import { type UseQueryResult, useQuery } from "@tanstack/react-query";
import { createDataSelector, createResourceCollectionSelector } from "jsonapi-zod-query";
import type {
    MRT_ColumnFiltersState,
    MRT_PaginationState,
    MRT_SortingState,
} from "material-react-table";

const resourceSelector = createResourceCollectionSelector({
    type: "phoneNumber",
    attributesSchema: phoneNumberSchema,
});

const phoneNumbersSelector = createDataSelector(resourceSelector);

export type PhoneNumbers = ReturnType<typeof phoneNumbersSelector>;
export type MrtPhoneNumber = PhoneNumbers[number];

type Props = {
    personUUID: string;
    columnFilters: MRT_ColumnFiltersState;
    globalFilter: string;
    pagination: MRT_PaginationState;
    sorting: MRT_SortingState;
    enabled?: boolean;
};

export const useFetchMrtPhoneNumbersForPerson = ({
    personUUID,
    columnFilters,
    globalFilter,
    pagination,
    sorting,
    enabled = true,
}: Props): UseQueryResult<PhoneNumbers> => {
    const fetch = useAuthenticatedFetch();

    return useQuery({
        enabled: enabled,
        queryKey: [
            "person-phone-number-table-data",
            personUUID,
            columnFilters,
            globalFilter,
            pagination.pageIndex,
            pagination.pageSize,
            sorting,
        ],
        queryFn: async () => {
            const fetchURL = apiUrl("/person/phone-number/for-person");

            const response = await fetch(fetchURL.href, {
                method: "POST",
                headers: {
                    "Content-Type": "application/json",
                },
                body: JSON.stringify({
                    personUUID: personUUID,
                    start: pagination.pageIndex * pagination.pageSize,
                    size: pagination.pageSize,
                    filters: columnFilters ?? [],
                    globalFilter: globalFilter ?? "",
                    sorting: sorting ?? [],
                }),
            });

            return await response.json();
        },
        select: phoneNumbersSelector,
    });
};
